import { useCallback, useEffect, useRef, useState } from 'react';
import { ID_PATTERNS } from '@find-my-part/types';
import { widgetApi, type MyQuestionItem } from '../api/client';
import { useMyQuestionsViewerEvidenceReporter, type MyQuestionsViewerEvidence } from '../viewer';

export type MyQuestionsLoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly questions: NormalizedMyQuestionItem[] }
  | { readonly status: 'unavailable' }
  | {
      readonly status: 'error';
      readonly message: string;
      readonly code?: string;
    };

export interface UseMyQuestionsResult {
  readonly loadState: MyQuestionsLoadState;
  /** Start a fresh request. Superseded work is aborted and cannot paint. */
  readonly retry: () => void;
}

const INITIAL_STATE: MyQuestionsLoadState = { status: 'loading' };

const PROTOCOL_ERROR_STATE: MyQuestionsLoadState = Object.freeze({
  status: 'error',
  message: 'Unable to load your questions. Please try again later.',
  code: 'PROTOCOL_ERROR',
});

export type NormalizedMyQuestionItem = Omit<MyQuestionItem, 'createdAt'> & {
  readonly createdAt: number;
};

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function parseCreatedAt(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 && !Number.isNaN(new Date(value).getTime())
      ? value
      : null;
  }
  if (typeof value !== 'string') return null;

  const timestamp = Date.parse(value);
  if (!Number.isSafeInteger(timestamp) || timestamp < 0) return null;

  // A Date serialized by the Worker is canonical UTC ISO. Exact round-trip
  // validation rejects locale strings, offsets, and normalized garbage.
  return new Date(timestamp).toISOString() === value ? timestamp : null;
}

function parseMyQuestionItem(value: unknown): NormalizedMyQuestionItem | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  if (
    !hasOwn(record, 'questionId') ||
    typeof record.questionId !== 'string' ||
    !ID_PATTERNS.QUESTION.test(record.questionId) ||
    !hasOwn(record, 'title') ||
    typeof record.title !== 'string' ||
    !hasOwn(record, 'status') ||
    typeof record.status !== 'string' ||
    !hasOwn(record, 'createdAt') ||
    !hasOwn(record, 'imageCount') ||
    typeof record.imageCount !== 'number' ||
    !Number.isSafeInteger(record.imageCount) ||
    record.imageCount < 0
  ) {
    return null;
  }

  const createdAt = parseCreatedAt(record.createdAt);
  if (createdAt === null) return null;

  return {
    questionId: record.questionId,
    title: record.title,
    status: record.status,
    createdAt,
    imageCount: record.imageCount,
  };
}

function resolveApiFailure(error: unknown): MyQuestionsLoadState {
  if (typeof error !== 'object' || error === null || Array.isArray(error)) {
    return PROTOCOL_ERROR_STATE;
  }

  const record = error as Record<string, unknown>;
  if (
    !hasOwn(record, 'message') ||
    typeof record.message !== 'string' ||
    !hasOwn(record, 'code') ||
    typeof record.code !== 'string'
  ) {
    return PROTOCOL_ERROR_STATE;
  }

  return {
    status: 'error',
    message: record.message,
    code: record.code,
  };
}

type ResolvedMyQuestionsEnvelope =
  | { readonly kind: 'success'; readonly data: unknown }
  | { readonly kind: 'failure'; readonly state: MyQuestionsLoadState };

/**
 * Fail closed at the outer private-response boundary before inspecting either
 * branch. The discriminator and its matching payload must be own properties;
 * truthy values and prototype-provided fields are not protocol evidence.
 */
function resolveMyQuestionsEnvelope(response: unknown): ResolvedMyQuestionsEnvelope {
  if (typeof response !== 'object' || response === null || Array.isArray(response)) {
    return { kind: 'failure', state: PROTOCOL_ERROR_STATE };
  }

  const record = response as Record<string, unknown>;
  if (!hasOwn(record, 'success') || typeof record.success !== 'boolean') {
    return { kind: 'failure', state: PROTOCOL_ERROR_STATE };
  }

  if (record.success) {
    return hasOwn(record, 'data')
      ? { kind: 'success', data: record.data }
      : { kind: 'failure', state: PROTOCOL_ERROR_STATE };
  }

  return hasOwn(record, 'error')
    ? { kind: 'failure', state: resolveApiFailure(record.error) }
    : { kind: 'failure', state: PROTOCOL_ERROR_STATE };
}

/**
 * Runtime-check the rollout-skew field before it becomes presentation state.
 *
 * `false` is the sole unavailable-history signal. `true` and an omitted or
 * `undefined` field retain the legacy list/empty behavior. Any other value is
 * a malformed protocol response and fails closed without reflecting it into
 * customer-visible copy.
 */
function resolveHistoryState(data: unknown): MyQuestionsLoadState {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return PROTOCOL_ERROR_STATE;
  }

  const record = data as Record<string, unknown>;
  if (!hasOwn(record, 'questions') || !Array.isArray(record.questions)) {
    return PROTOCOL_ERROR_STATE;
  }

  const questions: NormalizedMyQuestionItem[] = [];
  for (const value of record.questions) {
    const question = parseMyQuestionItem(value);
    if (!question) return PROTOCOL_ERROR_STATE;
    questions.push(question);
  }

  const hasHistoryAvailable = hasOwn(record, 'historyAvailable');
  const historyAvailable = hasHistoryAvailable ? record.historyAvailable : undefined;

  if (historyAvailable !== undefined && typeof historyAvailable !== 'boolean') {
    return PROTOCOL_ERROR_STATE;
  }

  if (historyAvailable === false) return { status: 'unavailable' };

  return {
    status: 'success',
    questions,
  };
}

/**
 * Race-safe, non-caching resource controller for cookie-authenticated
 * `/my-questions` data.
 *
 * A generation remains the final authority even when a transport or test
 * double ignores AbortSignal. Mount and retry intentionally share the same
 * request path, and unmount invalidates that generation before aborting it.
 */
export function useMyQuestions(isOnline: boolean): UseMyQuestionsResult {
  const [loadState, setLoadState] = useState<MyQuestionsLoadState>(INITIAL_STATE);
  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const isOnlineRef = useRef(isOnline);
  const reportViewerEvidence = useMyQuestionsViewerEvidenceReporter();
  const reporterRef = useRef(reportViewerEvidence);

  // Keep the stable request path current without turning connectivity or a
  // provider rerender into an implicit retry. The UI owns explicit recovery.
  useEffect(() => {
    isOnlineRef.current = isOnline;
    reporterRef.current = reportViewerEvidence;
  }, [isOnline, reportViewerEvidence]);

  const report = useCallback((evidence: MyQuestionsViewerEvidence): void => {
    reporterRef.current(evidence);
  }, []);

  const request = useCallback(async (): Promise<void> => {
    const generation = ++generationRef.current;

    controllerRef.current?.abort();
    controllerRef.current = null;
    report({ type: 'request-started' });
    setLoadState(INITIAL_STATE);

    if (!widgetApi.isInitialized()) {
      report({ type: 'request-failed' });
      setLoadState({
        status: 'error',
        message: 'Unable to load questions: widget not properly configured',
        code: 'WIDGET_INIT_ERROR',
      });
      return;
    }

    if (!isOnlineRef.current) {
      report({ type: 'request-failed' });
      setLoadState({
        status: 'error',
        message:
          'Unable to load questions: you appear to be offline. Please check your connection and try again.',
        code: 'OFFLINE',
      });
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const response = await widgetApi.questions.getMyQuestions({
        signal: controller.signal,
      });

      if (controller.signal.aborted || generationRef.current !== generation) return;

      const envelope = resolveMyQuestionsEnvelope(response);
      if (envelope.kind === 'failure') {
        report({ type: 'request-failed' });
        setLoadState(envelope.state);
        return;
      }

      const nextState = resolveHistoryState(envelope.data);
      if (nextState.status === 'error') {
        report({ type: 'request-failed' });
        setLoadState(nextState);
        return;
      }

      report({ type: 'response-received', data: envelope.data });
      setLoadState(nextState);
    } catch (error) {
      if (controller.signal.aborted || generationRef.current !== generation) return;

      report({ type: 'request-failed' });
      setLoadState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to load questions',
        code: 'NETWORK_ERROR',
      });
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [report]);

  const retry = useCallback((): void => {
    // `request` handles every rejection internally, so the event handler cannot
    // create an unhandled promise while still exposing a synchronous callback.
    void request();
  }, [request]);

  useEffect(() => {
    void request();

    return () => {
      generationRef.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [request]);

  return { loadState, retry };
}
